import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Button } from "./ui/button";

export const CATEGORIES = ["Hair", "Nails", "Facials", "Threading", "Waxing", "Laser Hair Removal"];

const EMPTY = {
  category: "Hair", name: "", price: "", price_label: "", regular_price_label: "",
  duration_min: 60, description: "", is_special: false,
};

const inputCls = "bg-[#141414] border-white/10 text-white rounded-sm focus-visible:ring-0 focus-visible:border-[#FF2079]";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] tracking-[0.25em] uppercase text-[#A09CA3]">{label}</Label>
      {children}
    </div>
  );
}

export default function ServiceDialog({ open, service, onClose, onSave, saving }) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm(service ? { ...EMPTY, ...service } : EMPTY);
  }, [open, service]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    const label = /^\$\d+(\.\d+)?$/.test((form.price_label || "").trim()) ? "" : form.price_label;
    onSave({ ...form, price_label: label, price: Number(form.price), duration_min: Number(form.duration_min) });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="service-dialog" className="bg-[#0E0E0E] border border-white/10 text-white max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl text-white font-light">
            {service ? "Edit service" : "Add service"}
          </DialogTitle>
          <DialogDescription className="text-[#A09CA3]">Changes show on the website right away.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <Field label="Category">
            <select data-testid="service-category-input" value={form.category} onChange={set("category")} className={`${inputCls} w-full h-10 px-3 border`}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Name">
            <Input data-testid="service-name-input" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Price ($)">
              <Input data-testid="service-price-input" required type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className={inputCls} />
            </Field>
            <Field label="Minutes">
              <Input data-testid="service-duration-input" required type="number" min="5" value={form.duration_min} onChange={set("duration_min")} className={inputCls} />
            </Field>
          </div>
          <Field label="Price shown (optional, e.g. $20 & above)">
            <Input data-testid="service-price-label-input" value={form.price_label} onChange={set("price_label")} className={inputCls} />
          </Field>
          <Field label="Description">
            <Textarea data-testid="service-description-input" value={form.description} onChange={set("description")} className={inputCls} />
          </Field>
          <div className="flex items-center justify-between glass-card rounded-sm p-4">
            <div>
              <div className="text-sm text-white">Feature as special offer</div>
              <div className="text-xs text-[#A09CA3]">Shows in the homepage deals section</div>
            </div>
            <Switch data-testid="service-special-switch" checked={form.is_special} onCheckedChange={(v) => setForm((f) => ({ ...f, is_special: v }))} />
          </div>
          {form.is_special && (
            <Field label="Regular price (shown crossed out)">
              <Input data-testid="service-regular-price-input" value={form.regular_price_label} onChange={set("regular_price_label")} className={inputCls} />
            </Field>
          )}
          <Button data-testid="service-save-btn" type="submit" disabled={saving} className="w-full pink-glow-btn bg-[#FF2079] hover:bg-[#D91666] text-white rounded-sm h-11 text-[11px] tracking-[0.28em] uppercase">
            {saving ? "Saving…" : "Save service"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
